import * as React from 'react';
import { FC } from 'react';
import {
  Badge,
  Black,
  Blue80,
  Button,
  FileUploadItem,
  Gray20,
  Gray100,
  Red90,
  White,
  message,
  typeCompact14MediumProperties,
  typeCompact14RegularProperties,
  typeCompact12RegularProperties,
} from '@traceair/tds';
import { classes, stylesheet } from 'typestyle';
import { CopyLinkIcon } from '@traceair/webapp-icons';
import { InvoiceSubmission } from './types';
import { SectionHeader } from './components/SectionHeader';
import { InvoicePageHeader } from './components/InvoicePageHeader';
import { ItemRow } from './components/ItemRow';
import { GcpMaterialRow } from './components/GcpMaterialRow';
import {
  TimeMaterialsDateHeader,
  TimeMaterialsCategoryHeader,
} from './components/time_materials/TimeMaterialsDateGroup';
import getPilotApiService from '../../../api_services/PilotApiService';
import Logger from '../../../libs/logger/Logger';
import {
  formatApiDate,
  getStatusBadgeProps,
  buildSections,
  buildCustomRows,
  buildFixDateGroups,
  buildConsultantGcpRateInfo,
  getGcpItemAmount,
  groupBySiteSummary,
  buildFlightsSubtitle,
  buildGcpSubtitle,
  buildTimeMaterialsAmountsByDate,
  isFixedFeeInvoice,
} from './InvoicingUtils';
import type { TimeMaterialsDateGroup } from './InvoicingUtils';
import { sharedInvoicingCss } from './styles/shared';
import { trackAction } from '../../../libs/analytics/ActionTracking';

type Props = {
  invoice: InvoiceSubmission;
  onBack: () => void;
};

export const InvoiceDetailReadonlyView: FC<Props> = ({ invoice, onBack }) => {
  const raw = invoice.rawInvoice;

  const [isCancelling, setIsCancelling] = React.useState(false);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({
    scan: true,
    pano: true,
    lidar: true,
    gcp_services: true,
    other: true,
  });

  const isFixedFee = isFixedFeeInvoice(raw);
  const sections = React.useMemo(() => (isFixedFee && raw ? buildSections(raw) : []), [raw, isFixedFee]);
  const fixDateGroups = React.useMemo(() => (!isFixedFee && raw ? buildFixDateGroups(raw) : []), [raw, isFixedFee]);
  const customRows = React.useMemo(() => (raw ? buildCustomRows(raw) : []), [raw]);
  // Per-day flight/mileage amounts come from `timeMaterialsEntries`.
  const { flightsCostsByDate, mileageByDate } = React.useMemo(
    () => buildTimeMaterialsAmountsByDate(raw?.timeMaterialsEntries),
    [raw],
  );
  // T&M flight/mileage totals are summed from the per-day amounts so the Total always matches the
  // per-day lines shown below. `submittedTotals` can't serve here: it has no mileage category at all
  // — mileage lands in `gcpMaterialsCosts`, inseparable from materials.
  const timeMaterialsFlightsTotal = Object.values(flightsCostsByDate).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const timeMaterialsMileageTotal = Object.values(mileageByDate).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const otherItems: Array<{ label: string; price: number }> = raw?.otherCharges ?? [];
  // Summed from the listed items, NOT from `submittedTotals.otherCosts`: that aggregate is the
  // service's catch-all `other` category, which also carries the cost of every video/misc mission —
  // missions this view already counts inside their own sections. Reading it here both showed a
  // section header larger than the rows under it and double-counted those missions in the total.
  const otherCostTotal = otherItems.reduce((s, i) => s + i.price, 0);
  // Shown only when there are items to show. Keying off `otherCosts` instead used to render an
  // "Other / No items" section for any invoice with a video/misc mission, since those feed that
  // aggregate — and now that the header sums the items, it would have read $0.
  const hasOther = otherItems.length > 0;

  // The T&M GCP amount for one day, summed from the missions themselves rather than read from
  // `submittedTotals.gcpServicesCosts`. That aggregate isn't a services-only figure: a day's
  // `gcp_services` entry carries materials folded in, so adding the extra costs on top of it would
  // count materials twice. Shared by the invoice total and the per-day rows below so the two can't
  // disagree, and correct for invoices of either shape since the missions are the same either way.
  const gcpTotalForDate = (group: TimeMaterialsDateGroup) => ({
    services: group.gcpItems
      .filter((item) => !item.isFailed)
      .reduce(
        (s, item) =>
          s + getGcpItemAmount(item.cost, item.consultantGcpServiceHourlyRate, item.consultantGcpServiceTimeInHours),
        0,
      ),
    extraCosts: group.gcpItems.flatMap((item) => item.extraCosts ?? []).reduce((s, e) => s + e.amount, 0),
  });

  const timeMaterialsGcpTotal = React.useMemo(
    () =>
      fixDateGroups.reduce((sum, group) => {
        const { services, extraCosts } = gcpTotalForDate(group);
        return sum + services + extraCosts;
      }, 0),
    [fixDateGroups],
  );

  const totalNum = raw
    ? (!isFixedFee
        ? // timeMaterialsGcpTotal already includes each GCP item's extra costs, and mileage is in
          // timeMaterialsMileageTotal — so no separate extraCosts term here. The legacy lump sum
          // arrives via customRows' "GCP Materials" row below, which buildCustomRows suppresses for
          // any invoice whose gcpMaterialsCosts the service derived from these same line items.
          timeMaterialsFlightsTotal + timeMaterialsGcpTotal + timeMaterialsMileageTotal
        : sections.reduce((s, sec) => s + (sec.total ?? 0), 0)) +
      customRows.reduce((s, r) => s + r.amount, 0) +
      otherCostTotal
    : null;
  const totalDisplay = totalNum != null ? `$${totalNum.toLocaleString()}` : invoice.total;

  const toggleSection = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }));

  async function handleCancel() {
    trackAction('Cancel invoice');
    setIsCancelling(true);
    try {
      await getPilotApiService().cancelInvoice(invoice.id);
      message.success({ title: 'Invoice cancelled', closable: true, theme: 'dark' });
      onBack();
    } catch (err) {
      Logger.error(err as Error | string);
      message.error({
        title: 'Failed to cancel invoice',
        description: 'Please try again or contact support.',
        closable: true,
        theme: 'dark',
      });
      setIsCancelling(false);
    }
  }

  const css = { ...sharedInvoicingCss, ...localCss };

  return (
    <div className={css.page}>
      <InvoicePageHeader
        title={invoice.invoiceName}
        onBack={() => {
          trackAction('Navigate back from invoice detail');
          onBack();
        }}
        badge={
          <span style={{ flexShrink: 0 }}>
            <Badge
              label={invoice.status === 'Paid' && raw?.paidOn ? `Paid ${formatApiDate(raw.paidOn)}` : invoice.status}
              size="l"
              {...getStatusBadgeProps(invoice.status)}
            />
          </span>
        }
      />

      <div className={css.periodSection}>
        <div className={css.periodLabel}>INVOICE PERIOD</div>
        <div className={localCss.periodDates}>{invoice.period}</div>
      </div>

      <div className={css.costCard}>
        {isFixedFee &&
          sections.map((section) => (
            <div key={section.id}>
              <SectionHeader
                section={section}
                isExpanded={expanded[section.id] ?? true}
                onToggle={() => toggleSection(section.id)}
              />
              {(expanded[section.id] ?? true) &&
                (section.items.length === 0 ? (
                  <p className={css.emptySectionMsg}>No items</p>
                ) : (
                  <div className={css.sectionItemsContainer}>
                    {section.items.map((item) => (
                      <div key={item.id}>
                        <ItemRow item={item} />
                        {item.extraCosts?.map((mat, idx) => (
                          <GcpMaterialRow key={idx} readOnly label={mat.description} amount={mat.amount} />
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          ))}

        {!isFixedFee &&
          fixDateGroups.map((group) => {
            const isDateExpanded = expanded[group.dateKey] !== false;
            const isFlightsExpanded = expanded[`${group.dateKey}-flights`] === true;
            const isGcpExpanded = expanded[`${group.dateKey}-gcp`] === true;

            const { services: gcpItemsTotal, extraCosts: gcpExtraCostsTotalForDate } = gcpTotalForDate(group);
            const flightsCostForDate = parseFloat(flightsCostsByDate[group.dateKey] || '0');
            const mileageForDate = parseFloat(mileageByDate[group.dateKey] || '0');

            const flightsSubtitle = buildFlightsSubtitle(group.scanPanoLidarItems);
            const gcpSubtitle = buildGcpSubtitle(group.gcpItems);

            return (
              <TimeMaterialsDateHeader
                key={group.dateKey}
                displayDate={group.displayDate}
                total={flightsCostForDate + mileageForDate + gcpItemsTotal + gcpExtraCostsTotalForDate}
                isExpanded={isDateExpanded}
                onToggle={() => toggleSection(group.dateKey)}
              >
                {group.scanPanoLidarItems.length > 0 && (
                  <TimeMaterialsCategoryHeader
                    title="Scans, panoramas price"
                    amount={flightsCostForDate}
                    subtitle={flightsSubtitle || undefined}
                    isExpanded={isFlightsExpanded}
                    onToggle={() => toggleSection(`${group.dateKey}-flights`)}
                  >
                    {groupBySiteSummary(group.scanPanoLidarItems).map((merged, idx, arr) => (
                      <ItemRow
                        key={idx}
                        hideDate
                        isLast={idx === arr.length - 1}
                        item={{
                          id: String(idx),
                          date: '',
                          siteName: merged.project,
                          customer: merged.account,
                          status: merged.isFailed ? 'failed' : merged.isOnReview ? 'on_review' : 'billable',
                          rateInfo: merged.summary,
                        }}
                      />
                    ))}
                  </TimeMaterialsCategoryHeader>
                )}

                {group.gcpItems.length > 0 && (
                  <TimeMaterialsCategoryHeader
                    title="GCP services"
                    subtitle={gcpSubtitle}
                    isExpanded={isGcpExpanded}
                    onToggle={() => toggleSection(`${group.dateKey}-gcp`)}
                    amount={gcpItemsTotal + gcpExtraCostsTotalForDate}
                  >
                    {group.gcpItems.map((item, itemIdx, itemsArr) => (
                      <div key={item.id}>
                        <ItemRow
                          hideDate
                          isLast={itemIdx === itemsArr.length - 1}
                          item={{
                            id: item.id,
                            date: '',
                            siteName: item.project,
                            customer: item.account,
                            status: item.isFailed ? 'failed' : 'billable',
                            category: item.category,
                            itemCount: item.itemCount,
                            rateInfo: item.isFailed
                              ? undefined
                              : buildConsultantGcpRateInfo(
                                  item.consultantGcpServiceHourlyRate,
                                  item.consultantGcpServiceTimeInHours,
                                ),
                            amount: item.isFailed
                              ? undefined
                              : getGcpItemAmount(
                                  item.cost,
                                  item.consultantGcpServiceHourlyRate,
                                  item.consultantGcpServiceTimeInHours,
                                ),
                          }}
                        />
                        {item.extraCosts?.map((mat, idx) => (
                          <GcpMaterialRow
                            key={idx}
                            timeMaterials
                            readOnly
                            label={mat.description}
                            amount={mat.amount}
                          />
                        ))}
                      </div>
                    ))}
                  </TimeMaterialsCategoryHeader>
                )}

                <TimeMaterialsCategoryHeader title="Mileage" amount={mileageForDate} />
              </TimeMaterialsDateHeader>
            );
          })}

        {hasOther && (
          <div>
            <SectionHeader
              section={{ id: 'other-costs', label: 'Other', items: [], total: otherCostTotal }}
              isExpanded={expanded['other-costs'] ?? true}
              onToggle={() => toggleSection('other-costs')}
              subtitle="Additional expenses"
              className={classes(
                css.otherSectionHeader,
                (expanded['other-costs'] ?? true) && css.otherSectionHeaderExpanded,
              )}
            />
            {(expanded['other-costs'] ?? true) &&
              (otherItems.length === 0 ? (
                <p className={css.emptySectionMsg}>No items</p>
              ) : (
                <div>
                  {otherItems.map((item, idx) => (
                    <div key={idx} className={css.lineItem}>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      <span className={css.itemAmount}>${item.price.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {customRows.length > 0 && (
          <>
            {customRows.map((field) => (
              <div key={field.id} className={css.customFieldRow}>
                <div className={css.customFieldInfo}>
                  <span className={css.customFieldLabel}>{field.label}</span>
                  <span className={css.customFieldDesc}>{field.desc}</span>
                </div>
                <span className={classes(localCss.customFieldAmount, field.amount < 0 && localCss.negativeAmount)}>
                  {field.amount < 0
                    ? `-$${Math.abs(field.amount).toLocaleString()}`
                    : `$${Math.abs(field.amount).toLocaleString()}`}
                </span>
              </div>
            ))}
          </>
        )}

        <div className={css.cardDivider} />
        <div className={css.totalRow}>
          <span className={css.totalLabel}>Total</span>
          <span className={css.totalAmount}>{totalDisplay}</span>
        </div>
      </div>

      <div className={css.outerSection}>
        <div className={css.outerSectionHead}>
          <div className={css.outerSectionTitle}>Upload Invoice</div>
          <div className={css.outerSectionSubtitle}>Must match the items listed above</div>
        </div>
        {raw?.invoiceDoc ? (
          <FileUploadItem
            file={{
              // pilot-api's mapInvoiceDoc returns null unless the url exists and always backfills
              // id and filename (from the url / its last segment), so all three are guaranteed here.
              uid: raw.invoiceDoc.id,
              name: raw.invoiceDoc.filename,
              size: raw.invoiceDoc.size,
              type: raw.invoiceDoc.type,
              status: 'done',
            }}
            onDownload={() => {
              trackAction('Download invoice PDF');
              window.open(raw!.invoiceDoc!.url, '_blank');
            }}
          />
        ) : (
          <span className={localCss.noDocumentText}>No document attached</span>
        )}
      </div>

      {raw?.paymentLink && (
        <div className={css.outerSection}>
          <div className={css.outerSectionHead}>
            <div className={css.outerSectionTitle}>Payment info</div>
            <div className={css.outerSectionSubtitle}>Select the payment method you prefer</div>
          </div>
          <div className={localCss.paymentLinkDisplay}>
            <CopyLinkIcon size={16} color={Gray100} />
            <span className={localCss.paymentLinkText}>{raw.paymentLink}</span>
          </div>
        </div>
      )}

      {/* Cancel is offered only while the invoice is still awaiting ops approval. Gates on the RAW
          backend status because the mapped `invoice.status` ('In Review') also covers
          `returned_by_accountant` — a return to the OPS manager, not the pilot — which must not be
          pilot-cancellable. Once approved (awaiting payment), paid, cancelled, or sent back for a
          fix, cancelling is no longer the pilot's action. */}
      {raw?.status === 'waiting_ops_approval' && (
        <div className={css.footer}>
          <Button
            style="secondary"
            size="m"
            type="danger"
            disabled={isCancelling}
            onClick={handleCancel}
            text={isCancelling ? 'Cancelling…' : 'Cancel Invoice'}
          />
        </div>
      )}
    </div>
  );
};

const localCss = stylesheet({
  periodDates: { ...typeCompact14MediumProperties, color: Black },
  customFieldAmount: { ...typeCompact14MediumProperties, color: Black, flexShrink: 0 },
  negativeAmount: { color: Red90 },
  noDocumentText: { ...typeCompact12RegularProperties, color: Gray100 },
  paymentLinkDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    border: `1px solid ${Gray20}`,
    borderRadius: 10,
    backgroundColor: White,
  },
  paymentLinkText: { ...typeCompact14RegularProperties, color: Blue80 },
});
